import { NewRecord } from '@/features/content-registration/state-machines/create-record.machine';
import Ajv from 'ajv';
import { schema as payloadSchema } from '@content-registration/config/ContentRegistrationRecordPayloadSchema';
import {
  ContentRegistrationFormPayload,
  ContentRegistrationFormSchema,
  ContentRegistrationType,
  ContentRegistrationRecord,
  isRecordType,
} from '@/common/types';
import { generateUuid } from './ids';
import { getFormDataWithDefaults } from '@content-registration/utils/formDataDefaults';
import { migrateFormDataLanguageCodes } from '@content-registration/utils/crossrefLanguageCodes';
import cloneDeep from 'lodash/cloneDeep';
import { FormData } from '@content-registration/types';
import { useRecordStore } from '@/features/content-registration/stores/records';
import { getSchemaForFormType } from '@content-registration/utils/schemaFactory';
import { useSessionStore } from '@/stores/session';
import { Sender } from 'xstate';
import { Ref } from 'vue';
import { readUploadedFileAsText } from './file';

const DEFAULT_RECORD_NAME = 'NOT_NAMED';
const DEFAULT_RECORD_EXTENSION = 'json';

export const createRecordFilename = (record: NewRecord) => {
  return `${record.recordName}.${record.recordExtension}`;
};

export const recordWithUuid = (
  record: ContentRegistrationFormPayload
): ContentRegistrationFormPayload => {
  if (!record.recordId) {
    return { ...record, recordId: generateUuid() };
  }

  return record;
};

export const stampRecordWithNewUuid = (
  record: ContentRegistrationFormPayload
): ContentRegistrationFormPayload => {
  return { ...record, recordId: generateUuid() };
};

const normalizeRecordType = (recordType: string): ContentRegistrationType => {
  const loweredRecordType = recordType.toLowerCase();

  if (isRecordType(loweredRecordType)) {
    return loweredRecordType;
  } else {
    throw new Error(`Invalid record type: ${loweredRecordType}`);
  }
};

export const loadRecordFromFile = async (file: File) => {
  const fileAsText = await readUploadedFileAsText(file);
  if (typeof fileAsText !== 'string') {
    throw new Error('There was an error reading your file, please try again.');
  }

  let record: ContentRegistrationFormPayload;
  try {
    record = JSON.parse(fileAsText);
  } catch (e) {
    throw new Error('The uploaded file contains invalid JSON.');
  }

  record = stampRecordWithNewUuid(record);
  record.recordType = normalizeRecordType(record.recordType);

  const ajv = new Ajv();
  const validate = ajv.compile(payloadSchema);
  const valid = validate(record);
  if (!valid) {
    const errorMessages = validate.errors
      ?.map((error) => {
        return `${error.message
          ?.charAt(0)
          .toLocaleUpperCase()}${error.message?.slice(1)}`;
      })
      .join('. ');

    throw new Error(
      `There was an error validating your file, please check and try again. ${errorMessages}`
    );
  }

  if (record.formData) {
    record.formData = migrateFormDataLanguageCodes(cloneDeep(record.formData));
  }

  return record;
};

const getRecordNameOrDefault = (record: ContentRegistrationRecord) => {
  return record.recordName || DEFAULT_RECORD_NAME;
};

export const createRecordWithId = (
  id: string,
  type: ContentRegistrationType,
  data: FormData | null = null
): ContentRegistrationFormPayload => {
  const schema = getSchemaForFormType(type);
  const dataWithDefaults = getFormDataWithDefaults(type, data);
  return {
    recordId: id,
    recordType: type,
    formData: dataWithDefaults,
    schemaName: schema.title,
    schemaVersion: schema.version,
    recordExtension: DEFAULT_RECORD_EXTENSION,
    origin: { type: 'new' },
  };
};

export const mergeRecordDefaults = (
  input: ContentRegistrationRecord,
  schema: ContentRegistrationFormSchema
): ContentRegistrationFormPayload => {
  const recordNameOrDefault = getRecordNameOrDefault(input);
  return {
    recordId: input.recordId,
    recordName: recordNameOrDefault,
    recordType: input.recordType,
    formData: getFormDataWithDefaults(input.recordType, input.formData),
    fileName: createRecordFilename({
      recordExtension: DEFAULT_RECORD_EXTENSION,
      recordName: recordNameOrDefault,
      recordType: input.recordType,
    }),
    schemaName: schema.title,
    schemaVersion: schema.version,
    recordExtension: DEFAULT_RECORD_EXTENSION,
    // Rebuilt field-by-field: origin must be carried over or every load
    // would strip an imported draft's registered-chapter protection.
    origin: input.origin,
  };
};

export const persistRecord = (record) => {
  const store = useRecordStore();
  const recordWithId = recordWithUuid(record);
  store.saveDraft(recordWithId);
};

export const updateFormData = (data: FormData) => {
  const sessionStore = useSessionStore();
  sessionStore.updateFormData(data);
};

export const startRecordEdit = (
  record: ContentRegistrationFormPayload,
  formDataService: { state: Ref<any>; send: Sender<any> }
) => {
  const sessionStore = useSessionStore();
  sessionStore.setAndPersistCurrentRecord(record);
  formDataService.send({
    type: 'START_EDITING_NEW_RECORD',
  });
};

export const getRecordFromDraftsOrSession = (id: string) => {
  const recordStore = useRecordStore();
  const sessionStore = useSessionStore();
  const recordInDrafts = recordStore.drafts.get(id);
  const recordInSession = sessionStore.currentRecord;
  if (!recordInDrafts && recordInSession?.recordId === id) {
    console.debug(`No draft record, but matching session for record ${id}`);
    return recordInSession;
  }
  if (recordInDrafts) {
    console.debug(`Draft record for record ${id}`);
    if (recordInDrafts.recordId !== id) {
      console.error(
        `Record missmatch for id ${id} : ${recordInDrafts.recordId}`
      );
      return null;
    }
    return recordInDrafts;
  }
  return null;
};

export const getOrCreateRecord = (
  recordId: string,
  schema: ContentRegistrationFormSchema,
  type: ContentRegistrationType,
  formData?: FormData
): ContentRegistrationFormPayload => {
  const recordFromStore = getRecordFromDraftsOrSession(recordId);
  if (recordFromStore) {
    console.debug(`Found record ${recordId} in store`);
    return mergeRecordDefaults(recordFromStore, schema);
  }
  console.debug(
    `No draft record or matching session,  creating new record for ${recordId}`
  );
  const newRecord = createRecordWithId(recordId, type, formData || {});
  return mergeRecordDefaults(newRecord, schema);
};

